import { useCallback, useState } from 'react';
import {
  useMutation,
  useQuery,
  useQueryClient,
  keepPreviousData,
  type UseQueryOptions,
} from '@tanstack/react-query';
import { api } from 'lib/request/api';
import type { Options } from 'ky';
import { DEFAULT_PAGE_SIZE } from 'utils/page-sizes';
import { apiPath, toWorkspaceError } from 'pages/well-workspaces/utils';
import type {
  ComparisonKind,
  ComparisonRun,
  OwnershipRow,
  Paged,
  PagedFilters,
  RunsheetRow,
  WorkspaceEnvelope,
  WorkspacesListResponse,
} from './types';
import type { WellWorkspacesSummary } from 'types/well-workspace';
import type { CRMTractsResponse, SearchResult } from 'types/api-responses';
import { CRM_TRACTS_DEFAULT_PARAMS } from 'pages/crm-tracts-subtracts/utils';
import type { CrmTractSearchParams } from 'pages/crm-tracts-subtracts/atoms';
import {
  flattenResults,
  type CrmTractTableRow,
} from 'pages/crm-tracts-subtracts/crm-tracts-content';
import type { VestingDocumentsListResponse } from 'components/vesting-documents/types';
import { vestingDocumentsUrl } from 'components/vesting-documents/atoms';

const WORKSPACE_TYPE = 'TitleSupportWorkspace';

const base = (id: string | number) => `title_support_workspaces/${id}`;

export const workspacePath = (id: string | number) =>
  `/title_support_workspaces/${id}`;

// The wizard opens with this tract picked and searched for, so its row is on screen to name it.
export const newWorkspacePath = (tract: { id: string; name: string }) =>
  `/title_support_workspaces/new?${new URLSearchParams({
    tract_id: tract.id,
    tract_name: tract.name,
  })}`;

export const keys = {
  all: ['title-support-workspaces'] as const,
  list: (paging?: ListPaging) => {
    if (!paging) return [...keys.all, 'list'] as const;
    return [...keys.all, 'list', paging] as const;
  },
  summary: () => [...keys.all, 'summary'] as const,
  detail: (id: string | number) => [...keys.all, 'detail', String(id)] as const,
  paged: (id: string | number, resource: PagedResource) =>
    [...keys.all, 'paged', String(id), resource] as const,
  run: (id: string | number, runId: number | null) =>
    [...keys.all, 'run', String(id), runId] as const,
};

const run = async <T>(request: Promise<T>): Promise<T> => {
  try {
    return await request;
  } catch (error) {
    throw await toWorkspaceError(error);
  }
};

export interface ListPaging {
  page: number;
  pageSize: number;
  entityId?: number;
  filters?: Record<string, unknown>;
  sort?: { id: string; desc: boolean };
}

export const postWorkspace = (input: {
  name: string;
  tractIds: string[];
  vestingDocumentIds: number[];
}) =>
  run(
    api
      .post('workspaces', {
        json: {
          workspace: {
            type: WORKSPACE_TYPE,
            name: input.name,
            tract_ids: input.tractIds,
            vesting_document_ids: input.vestingDocumentIds,
          },
        },
      })
      .json<WorkspaceEnvelope>()
  );

export const postPromotion = (id: string | number) =>
  run(
    api
      .post(`workspaces/${id}/promotions`, { json: {} })
      .json<WorkspaceEnvelope>()
  );

export const postTracts = (id: string | number, salesforceTractIds: string[]) =>
  run(
    api
      .post(`${base(id)}/tracts`, {
        json: { salesforce_tract_ids: salesforceTractIds },
      })
      .json<unknown>()
  );

export const deleteWorkspace = (id: string | number) =>
  run(api.delete(`workspaces/${id}`).text());

export const deleteTract = (id: string | number, salesforceTractId: string) =>
  run(api.delete(`${base(id)}/tracts/${salesforceTractId}`).json<unknown>());

// `href` is the row's own scoped route; a PATCH there stages Verify.
export const patchStagedVerification = (href: string) =>
  run(api.patch(apiPath(href), { json: {} }).json<unknown>());

export const deletePendingChange = (
  id: string | number,
  kind: 'vesting' | 'tract_link',
  changeId: number
) => run(api.delete(`${base(id)}/pending_changes/${kind}/${changeId}`).text());

export const postSupportRetry = (id: string | number, supportId: number) =>
  run(api.post(`${base(id)}/supports/${supportId}/retry`, { json: {} }).text());

export interface PagedRows {
  runsheet: RunsheetRow;
  standard_mor: OwnershipRow;
}
export type PagedResource = keyof PagedRows;

// Computes synchronously; the answer is the finished run, failed or not.
export const postComparisonRun = <Run extends ComparisonRun>(
  id: string | number,
  kind: ComparisonKind
) =>
  run(api.post(`${base(id)}/comparison_runs`, { json: { kind } }).json<Run>());

export const postManualLink = (
  id: string | number,
  documentItemId: number,
  documentId: number
) =>
  run(
    api
      .post(`${base(id)}/manual_links`, {
        json: { document_item_id: documentItemId, document_id: documentId },
      })
      .text()
  );

export const deleteSupport = (id: string | number, supportId: number) =>
  run(api.delete(`${base(id)}/supports/${supportId}`).text());

export const deleteManualLink = (id: string | number, documentItemId: number) =>
  run(api.delete(`${base(id)}/manual_links/${documentItemId}`).text());

export const agentMessagesHref = (id: string | number) =>
  `/${base(id)}/agent_messages`;

// Writes and Pusher events invalidate, so a remount can paint from cache.
const STALE_TIME = 30_000;

export const fetchWorkspaces = (paging: ListPaging, signal?: AbortSignal) =>
  run(
    api
      .post('queries/workspaces', {
        json: {
          type: WORKSPACE_TYPE,
          page: paging.page,
          page_size: paging.pageSize,
          ...(paging.entityId !== undefined && {
            entity_id: paging.entityId,
          }),
          ...(paging.sort && {
            sort_attribute: paging.sort.id,
            sort_direction: paging.sort.desc ? 'desc' : 'asc',
          }),
          ...paging.filters,
        },
        signal,
      })
      .json<WorkspacesListResponse>()
  );

export const useWorkspaces = (paging: ListPaging) =>
  useQuery({
    queryKey: keys.list(paging),
    queryFn: ({ signal }) => fetchWorkspaces(paging, signal),
    retry: 1,
    placeholderData: keepPreviousData,
  });

export const fetchCrmTracts = (
  params: CrmTractSearchParams,
  signal?: AbortSignal,
  options?: Pick<Options, 'timeout'>
) =>
  run(
    api
      .post('queries/crm/tracts_and_subtracts', {
        json: params,
        signal,
        ...options,
      })
      .json<SearchResult<CRMTractsResponse>>()
  );

// One CRM tract's row by its Salesforce id, for a pick the tract search has not returned.
export const useCrmTractRow = (id: string | null) =>
  useQuery({
    queryKey: [...keys.all, 'crm-tract', id] as const,
    queryFn: ({ signal }) =>
      fetchCrmTracts(
        {
          ...CRM_TRACTS_DEFAULT_PARAMS,
          tractIds: [id ?? ''],
          page: 1,
          pageSize: 1,
        },
        signal
      ),
    enabled: !!id,
    staleTime: STALE_TIME,
    select: (data) =>
      flattenResults(data.results).find((row) => row.id === id) as
        CrmTractTableRow | undefined,
  });

interface OpenDraft {
  id: number;
  name: string;
  salesforceTractIds: string[];
}

// The open drafts already holding any of these tracts, less `exceptId`, so a draft can warn before sharing them.
export const useOpenDrafts = (tractIds: string[], exceptId?: string) =>
  useQuery({
    queryKey: [...keys.all, 'open-drafts', tractIds, exceptId] as const,
    queryFn: ({ signal }) =>
      run(
        api
          .get('title_support_workspaces/open_drafts', {
            searchParams: new URLSearchParams([
              ...tractIds.map((tractId) => ['tract_ids[]', tractId]),
              ...(exceptId ? [['except_id', exceptId]] : []),
            ]),
            signal,
          })
          .json<{ openDrafts: OpenDraft[] }>()
      ),
    enabled: tractIds.length > 0,
    placeholderData: keepPreviousData,
  });

// A CRM tract's vesting documents, as its own page lists them.
export const useTractVestingDocuments = (salesforceTractId: string) =>
  useQuery({
    queryKey: [...keys.all, 'tract-vesting', salesforceTractId] as const,
    queryFn: ({ signal }) =>
      run(
        api
          .get(
            apiPath(
              vestingDocumentsUrl({
                basePath: `/crm/tracts/${salesforceTractId}`,
              })
            ),
            { signal }
          )
          .json<VestingDocumentsListResponse>()
      ),
    staleTime: STALE_TIME,
  });

export const useSummary = () =>
  useQuery({
    queryKey: keys.summary(),
    queryFn: ({ signal }) =>
      run(
        api
          .get('workspaces/summary', {
            searchParams: { type: WORKSPACE_TYPE },
            signal,
          })
          .json<WellWorkspacesSummary>()
      ),
    retry: 1,
  });

export const useWorkspace = (
  id: string,
  options?: Pick<UseQueryOptions<WorkspaceEnvelope>, 'refetchInterval'>
) =>
  useQuery<WorkspaceEnvelope>({
    queryKey: keys.detail(id),
    queryFn: ({ signal }) =>
      run(api.get(`workspaces/${id}`, { signal }).json<WorkspaceEnvelope>()),
    retry: 1,
    staleTime: STALE_TIME,
    ...options,
  });

// Sent as `sort[]`, `direction[]` and `export[]`, as the filters are.
export interface PagedOptions {
  sort?: string;
  direction?: 'asc' | 'desc';
  export?: boolean;
}

export const fetchPaged = <Resource extends PagedResource>(
  id: string,
  resource: Resource,
  page: number,
  pageSize: number,
  filters: PagedFilters = {},
  text = '',
  options: PagedOptions = {},
  signal?: AbortSignal
) =>
  run(
    api
      .get(`${base(id)}/${resource}`, {
        searchParams: new URLSearchParams([
          ['page', String(page)],
          ['page_size', String(pageSize)],
          ...(text ? [['text', text]] : []),
          ...Object.entries(filters).flatMap(([param, values]) =>
            values.map((value) => [`${param}[]`, value])
          ),
          ...(options.sort ? [['sort[]', options.sort]] : []),
          ...(options.direction ? [['direction[]', options.direction]] : []),
          ...(options.export ? [['export[]', 'true']] : []),
        ]),
        signal,
      })
      .json<Paged<PagedRows[Resource]>>()
  );

export const usePaged = <Resource extends PagedResource>(
  id: string,
  resource: Resource,
  page: number,
  pageSize: number,
  filters: PagedFilters = {},
  text = '',
  options: PagedOptions = {},
  enabled = true
) =>
  useQuery({
    queryKey: [
      ...keys.paged(id, resource),
      page,
      pageSize,
      filters,
      text,
      options,
    ],
    queryFn: ({ signal }) =>
      fetchPaged(id, resource, page, pageSize, filters, text, options, signal),
    enabled,
    placeholderData: keepPreviousData,
    staleTime: STALE_TIME,
  });

export const useComparisonRun = <Run extends ComparisonRun>(
  id: string,
  runId: number | null
) =>
  useQuery({
    queryKey: keys.run(id, runId),
    queryFn: ({ signal }) =>
      run(
        api.get(`${base(id)}/comparison_runs/${runId}`, { signal }).json<Run>()
      ),
    enabled: runId !== null,
    staleTime: STALE_TIME,
  });

export const useRefreshWorkspace = () => {
  const queryClient = useQueryClient();
  return useCallback(
    () => queryClient.invalidateQueries({ queryKey: keys.all }),
    [queryClient]
  );
};

export const useWorkspaceWrite = <Vars>(
  mutationFn: (vars: Vars) => Promise<unknown>,
  onSuccess?: () => void
) => {
  const refresh = useRefreshWorkspace();
  return useMutation<unknown, Error, Vars>({
    mutationFn,
    retry: false,
    onSuccess: () => {
      refresh();
      onSuccess?.();
    },
  });
};

// Paging that starts again at page 1 when filterKey changes, in that render, so the old page is never requested.
export const useKeyedPaging = (filterKey: string) => {
  const [paging, setPaging] = useState({
    page: 1,
    pageSize: DEFAULT_PAGE_SIZE,
    filterKey,
  });
  const current =
    paging.filterKey === filterKey ? paging : { ...paging, page: 1, filterKey };
  if (current !== paging) setPaging(current);
  return {
    page: current.page,
    pageSize: current.pageSize,
    firstPage: () => setPaging({ ...current, page: 1 }),
    onPaginationChange: (state: { page: number; pageSize: number }) =>
      setPaging({ page: state.page, pageSize: state.pageSize, filterKey }),
  };
};

/**
 * Whether a paged table shows loading: a new page, sort or filter shows loading over the
 * rows it replaces; a refetch of the same key does not.
 *
 * @param {object} query - The paged query's result.
 * @param {boolean} query.isPending - Nothing to show yet: no data, and no rows held over.
 * @param {boolean} query.isFetching - A request is in flight.
 * @param {boolean} query.isPlaceholderData - The rows shown are the previous key's.
 * @return {boolean} True while the table should show loading.
 */
export const pagedLoading = (query: {
  isPending: boolean;
  isFetching: boolean;
  isPlaceholderData: boolean;
}) => query.isPending || (query.isFetching && query.isPlaceholderData);
